import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';

// CoinGecko API for ETH price
const COINGECKO_API_URL = 'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd';

interface CreatorEarningsData {
  totalWethEarned: string;
  totalCollections: number;
  recentEarnings: Array<{
    id: string;
    weth_earned: string;
    txn_hash: string;
    created_at: string;
  }>;
}

interface EthPriceCache {
  price: number;
  timestamp: number;
}

export function useCreatorEarnings(creatorAddress: string | undefined) {
  const [earningsData, setEarningsData] = useState<CreatorEarningsData | null>(null);
  const [ethPrice, setEthPrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch ETH price with caching
  const fetchEthPrice = async (): Promise<number> => {
    try {
      // Check cache first
      const cached = localStorage.getItem('eth_price_cache');
      if (cached) {
        const cache: EthPriceCache = JSON.parse(cached);
        const now = Date.now();

        // Use cache if less than 60 seconds old
        if (now - cache.timestamp < 60000) {
          return cache.price;
        }
      }

      // Fetch fresh price
      const response = await fetch(COINGECKO_API_URL);
      const data = await response.json();
      const price = data.ethereum.usd;

      // Cache the price
      const cache: EthPriceCache = {
        price,
        timestamp: Date.now()
      };
      localStorage.setItem('eth_price_cache', JSON.stringify(cache));

      return price;
    } catch (error) {
      console.error('Error fetching ETH price:', error);
      // Return cached price if available, otherwise 0
      const cached = localStorage.getItem('eth_price_cache');
      if (cached) {
        return JSON.parse(cached).price;
      }
      return 0;
    }
  };

  // Fetch creator earnings data
  const fetchEarningsData = async () => {
    if (!creatorAddress) {
      console.log('🔍 [CreatorEarnings] No creator address provided');
      setEarningsData(null);
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 [CreatorEarnings] Fetching earnings for:', creatorAddress);
      setLoading(true);
      setError(null);

      // Fetch creator earnings
      const { data: earnings, error: earningsError } = await supabase
        .from('creator_earnings')
        .select('*')
        .eq('creator_address', creatorAddress)
        .order('created_at', { ascending: false });

      console.log('🔍 [CreatorEarnings] Raw earnings data:', earnings);
      console.log('🔍 [CreatorEarnings] Earnings error:', earningsError);

      if (earningsError) {
        throw new Error(`Failed to fetch earnings: ${earningsError.message}`);
      }

      // Calculate totals - sum decimal WETH values (already in decimal format from DB)
      const totalWethEarned = earnings.reduce((sum, earning) => {
        const wethDecimal = parseFloat(earning.weth_earned);
        return (parseFloat(sum) + wethDecimal).toString();
      }, '0');

      const totalCollections = earnings.length;

      console.log('🔍 [CreatorEarnings] Calculated totals:', {
        totalWethEarned,
        totalCollections,
        earningsCount: earnings.length
      });

      // Get recent earnings (last 10)
      const recentEarnings = earnings.slice(0, 10).map(earning => ({
        id: earning.id,
        weth_earned: earning.weth_earned,
        txn_hash: earning.txn_hash,
        created_at: earning.created_at
      }));

      const earningsDataResult = {
        totalWethEarned,
        totalCollections,
        recentEarnings
      };

      console.log('🔍 [CreatorEarnings] Setting earnings data:', earningsDataResult);
      setEarningsData(earningsDataResult);

      // Fetch ETH price
      const price = await fetchEthPrice();
      setEthPrice(price);

    } catch (err) {
      console.error('Error fetching creator earnings:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchEarningsData();
  }, [creatorAddress]);

  // Refresh function
  const refetch = () => {
    fetchEarningsData();
  };

  return {
    earningsData,
    ethPrice,
    loading,
    error,
    refetch
  };
}

/**
 * Format earnings for display
 * Amount is already in decimal WETH format (not wei)
 */
export function formatEarnings(amount: string): string {
  const numAmount = parseFloat(amount);

  if (numAmount === 0) return '0';
  if (numAmount < 0.001) return '< 0.001';
  if (numAmount < 1) return numAmount.toFixed(3);
  if (numAmount < 100) return numAmount.toFixed(2);
  return numAmount.toFixed(1);
}